import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "../../api";
import { PageHeader, eur } from "../../components/common";
import { TrendingUp, TrendingDown, Target, ArrowLeft, RefreshCw, Pencil, Check, X, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { useAutoRefresh } from "../../lib/autoRefresh";

// Paleta de cores para as series (primeiro e total/global)
const SERIES_COLORS = [
  "#0f172a", // total - navy
  "#3b82f6", // azul
  "#10b981", // verde
  "#f59e0b", // amarelo
  "#8b5cf6", // violeta
  "#ef4444", // vermelho
  "#06b6d4", // ciano
  "#f97316", // laranja
  "#ec4899", // rosa
];

const PERIODS = [
  { label: "4 sem", weeks: 4 },
  { label: "8 sem", weeks: 8 },
  { label: "13 sem", weeks: 13 },
  { label: "26 sem", weeks: 26 },
  { label: "52 sem", weeks: 52 },
];

// ---------------------------------------------------------------------------
// Grafico SVG de linhas multiplas com linha de meta
// ---------------------------------------------------------------------------
function LineChart({ series, width = 680, height = 280, visibleIds, metas = {} }) {
  const [hover, setHover] = useState(null);
  const PAD = { top: 16, right: 24, bottom: 36, left: 60 };
  const W = width - PAD.left - PAD.right;
  const H = height - PAD.top - PAD.bottom;

  const activeSeries = series.filter((s) => !visibleIds || visibleIds.has(s.id));
  const allMetas = Object.values(metas).filter(Boolean);
  const allVals = [...activeSeries.flatMap((s) => s.points.map((p) => p.mrr)), ...allMetas];
  const maxV = Math.max(...allVals, 1);
  const minV = 0;
  const range = maxV - minV || 1;
  const nWeeks = series[0]?.points?.length || 0;

  const xOf = (i) => PAD.left + (i / Math.max(nWeeks - 1, 1)) * W;
  const yOf = (v) => PAD.top + (1 - (v - minV) / range) * H;

  const yTicks = Array.from({ length: 5 }, (_, i) => minV + (range * i) / 4);
  const xStep = nWeeks <= 13 ? 1 : nWeeks <= 26 ? 2 : 4;
  const xTicks = series[0]?.points
    ?.map((p, i) => ({ i, label: p.week }))
    .filter((_, i) => i % xStep === 0 || i === nWeeks - 1) || [];

  const hoverX = hover !== null ? xOf(hover) : null;
  const hoverPts = hover !== null ? activeSeries.map((s) => ({ ...s, pt: s.points[hover] })) : [];

  return (
    <div className="relative w-full">
      <svg viewBox={`0 0 ${width} ${height}`} className="w-full overflow-visible" style={{ touchAction: "none" }}
        onMouseLeave={() => setHover(null)}>
        {/* Grid horizontal */}
        {yTicks.map((v, i) => (
          <g key={i}>
            <line x1={PAD.left} y1={yOf(v)} x2={PAD.left + W} y2={yOf(v)} stroke="#e2e8f0" strokeWidth="1" />
            <text x={PAD.left - 6} y={yOf(v) + 4} textAnchor="end" fontSize="9" fill="#94a3b8" fontFamily="system-ui,sans-serif">
              {v >= 1000 ? `${(v / 1000).toFixed(1)}k` : v.toFixed(0)}
            </text>
          </g>
        ))}
        {/* Grid vertical leve */}
        {xTicks.map(({ i }) => (
          <line key={i} x1={xOf(i)} y1={PAD.top} x2={xOf(i)} y2={PAD.top + H} stroke="#f1f5f9" strokeWidth="1" />
        ))}
        {/* Eixo X */}
        {xTicks.map(({ i, label }) => (
          <text key={i} x={xOf(i)} y={PAD.top + H + 16} textAnchor="middle" fontSize="9" fill="#94a3b8" fontFamily="system-ui,sans-serif">
            {label}
          </text>
        ))}
        {/* Linhas de meta (horizontais, tracejadas) */}
        {activeSeries.map((s, si) => {
          const meta = metas[s.id];
          if (!meta) return null;
          const color = SERIES_COLORS[series.findIndex((x) => x.id === s.id) % SERIES_COLORS.length];
          const y = yOf(meta);
          return (
            <g key={`meta-${s.id}`}>
              <line x1={PAD.left} y1={y} x2={PAD.left + W} y2={y}
                stroke={color} strokeWidth="1" strokeDasharray="4,3" opacity="0.55" />
              <rect x={PAD.left + W - 32} y={y - 8} width={32} height={14} fill={color} opacity="0.1" rx="3" />
              <text x={PAD.left + W - 16} y={y + 3} textAnchor="middle" fontSize="8" fill={color} fontFamily="system-ui,sans-serif" fontWeight="600" opacity="0.8">
                meta
              </text>
            </g>
          );
        })}
        {/* Linhas de dados */}
        {activeSeries.map((s) => {
          const color = SERIES_COLORS[series.findIndex((x) => x.id === s.id) % SERIES_COLORS.length];
          const isDash = s.id === "__total__";
          const pts = s.points.map((p, i) => `${xOf(i)},${yOf(p.mrr)}`).join(" ");
          return (
            <g key={s.id}>
              <polyline points={pts} fill="none" stroke={color} strokeWidth={isDash ? 2 : 1.5}
                strokeLinejoin="round" strokeLinecap="round" strokeDasharray={isDash ? "6,3" : "none"} opacity="0.9" />
              <circle cx={xOf(s.points.length - 1)} cy={yOf(s.points[s.points.length - 1].mrr)}
                r="3" fill={color} stroke="white" strokeWidth="1.5" />
            </g>
          );
        })}
        {/* Linha de hover */}
        {hover !== null && (
          <line x1={hoverX} y1={PAD.top} x2={hoverX} y2={PAD.top + H} stroke="#94a3b8" strokeWidth="1" strokeDasharray="3,2" />
        )}
        {/* Areas de captura mouse */}
        {Array.from({ length: nWeeks }, (_, i) => (
          <rect key={i}
            x={i === 0 ? xOf(i) : xOf(i) - (xOf(i) - xOf(i - 1)) / 2}
            y={PAD.top} height={H} fill="transparent"
            width={i === 0 ? (xOf(1) - xOf(0)) / 2 : i === nWeeks - 1 ? (xOf(i) - xOf(i - 1)) / 2 : xOf(i) - xOf(i - 1)}
            onMouseEnter={() => setHover(i)} />
        ))}
        {/* Pontos de hover destacados */}
        {hover !== null && hoverPts.map((s) => {
          const color = SERIES_COLORS[series.findIndex((x) => x.id === s.id) % SERIES_COLORS.length];
          return <circle key={s.id} cx={hoverX} cy={yOf(s.pt.mrr)} r="4" fill={color} stroke="white" strokeWidth="1.5" />;
        })}
      </svg>
      {/* Tooltip */}
      {hover !== null && series[0]?.points?.[hover] && (
        <div className="absolute z-30 pointer-events-none"
          style={{ left: `${(xOf(hover) / width) * 100}%`, top: 0, transform: hover > nWeeks * 0.65 ? "translateX(-105%)" : "translateX(8px)" }}>
          <div className="bg-slate-900 text-white rounded-xl p-3 shadow-2xl text-[11px] min-w-[160px]">
            <div className="text-slate-400 text-[9px] uppercase tracking-wider mb-1.5">
              semana {series[0].points[hover].week}
            </div>
            {hoverPts.map((s) => {
              const color = SERIES_COLORS[series.findIndex((x) => x.id === s.id) % SERIES_COLORS.length];
              const meta = metas[s.id];
              const pct = meta ? Math.round(s.pt.mrr / meta * 100) : null;
              return (
                <div key={s.id} className="flex items-center justify-between gap-3 mb-1">
                  <div className="flex items-center gap-1.5 min-w-0">
                    <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: color }} />
                    <span className="text-slate-300 truncate text-[10px]">{s.nome}</span>
                  </div>
                  <div className="text-right">
                    <span className="font-semibold">{eur(s.pt.mrr)}</span>
                    {pct !== null && <span className="text-slate-400 text-[9px] ml-1">{pct}%</span>}
                    {!s.pt.real && <span className="text-amber-400 ml-0.5 text-[8px]">~</span>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Card de resumo com meta editavel e barra de progresso
// ---------------------------------------------------------------------------
function SummaryCard({ id, nome, summary, color, active, onClick, onSaveMeta, onSaveRegressao, canEdit }) {
  const [editing, setEditing] = useState(false);
  const [metaInput, setMetaInput] = useState("");
  const [editingReg, setEditingReg] = useState(false);
  const [regInput, setRegInput] = useState("");
  const up = summary.delta >= 0;
  const meta = summary.mrr_meta || 0;
  const pct = summary.pct_meta;
  const reg = summary.mrr_regressao_pct || 0;
  const progressColor = pct == null ? "#e2e8f0" : pct >= 100 ? "#22c55e" : pct >= 70 ? "#3b82f6" : pct >= 40 ? "#f59e0b" : "#ef4444";

  const startEdit = (e) => { e.stopPropagation(); setMetaInput(meta ? meta.toString() : ""); setEditing(true); };
  const cancelEdit = (e) => { e.stopPropagation(); setEditing(false); };
  const saveEdit = async (e) => {
    e.stopPropagation();
    const v = parseFloat(metaInput.replace(",", "."));
    if (isNaN(v) || v < 0) return toast.error("Valor invalido.");
    await onSaveMeta(id, v);
    setEditing(false);
  };

  const startEditReg = (e) => { e.stopPropagation(); setRegInput(reg ? reg.toString() : ""); setEditingReg(true); };
  const cancelEditReg = (e) => { e.stopPropagation(); setEditingReg(false); };
  const saveEditReg = async (e) => {
    e.stopPropagation();
    const v = parseFloat(regInput.replace(",", "."));
    if (isNaN(v) || v <= 0 || v > 100) return toast.error("Limiar deve estar entre 0 e 100.");
    await onSaveRegressao(id, v);
    setEditingReg(false);
  };

  return (
    <button onClick={onClick} data-testid={`summary-card-${id}`}
      className={`text-left p-3 rounded-xl border-2 transition-all w-full ${active ? "border-current shadow-md" : "border-transparent bg-slate-50 hover:bg-white hover:border-slate-200"}`}
      style={active ? { borderColor: color, background: `${color}08` } : {}}>
      <div className="flex items-center gap-1.5 mb-1">
        <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: color }} />
        <span className="text-[11px] font-semibold text-navy truncate flex-1">{nome}</span>
      </div>
      <div className="text-[18px] font-bold text-navy leading-none">{eur(summary.mrr)}</div>
      <div className="text-[10px] text-slate-400 mt-0.5">/mes</div>
      <div className={`flex items-center gap-1 mt-1.5 text-[10px] font-semibold ${up ? "text-green-600" : "text-red-500"}`}>
        {up ? <TrendingUp size={10} /> : <TrendingDown size={10} />}
        {up ? "+" : ""}{summary.delta.toLocaleString("pt-PT", { minimumFractionDigits: 0 })} ({up ? "+" : ""}{summary.pct}%)
      </div>
      {/* Meta MRR */}
      <div className="mt-2 border-t border-slate-100 pt-2">
        {editing ? (
          <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
            <span className="text-[10px] text-slate-400">Meta:</span>
            <input autoFocus value={metaInput} onChange={(e) => setMetaInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") saveEdit(e); if (e.key === "Escape") cancelEdit(e); }}
              className="w-20 text-[11px] border border-blue/40 rounded px-1.5 py-0.5 focus:outline-none focus:border-blue" />
            <button onClick={saveEdit} className="text-green-600 hover:text-green-700"><Check size={12} /></button>
            <button onClick={cancelEdit} className="text-slate-400 hover:text-slate-600"><X size={12} /></button>
          </div>
        ) : (
          <div className="flex items-center gap-1">
            <span className="text-[10px] text-slate-400">{meta ? `Meta: ${eur(meta)}` : "Sem meta"}</span>
            {canEdit && (
              <button onClick={startEdit} className="text-slate-300 hover:text-blue transition-colors ml-0.5">
                <Pencil size={9} />
              </button>
            )}
          </div>
        )}
        {/* Barra de progresso */}
        {meta > 0 && (
          <div className="mt-1.5">
            <div className="flex items-center justify-between mb-0.5">
              <span className="text-[9px] text-slate-400">Concretizacao</span>
              <span className="text-[9px] font-bold" style={{ color: progressColor }}>{pct ?? 0}%</span>
            </div>
            <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
              <div className="h-full rounded-full transition-all duration-700"
                style={{ width: `${Math.min(pct ?? 0, 100)}%`, background: progressColor }} />
            </div>
          </div>
        )}
        {/* Limiar de regressao MRR */}
        {canEdit && (
          <div className="mt-1.5 pt-1.5 border-t border-slate-100">
            {editingReg ? (
              <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                <AlertTriangle size={9} className="text-amber-400 flex-shrink-0" />
                <span className="text-[10px] text-slate-400">Alerta:</span>
                <input autoFocus value={regInput} onChange={(e) => setRegInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") saveEditReg(e); if (e.key === "Escape") cancelEditReg(e); }}
                  className="w-14 text-[11px] border border-amber-300 rounded px-1.5 py-0.5 focus:outline-none focus:border-amber-500"
                  placeholder="ex: 10" />
                <span className="text-[10px] text-slate-400">%</span>
                <button onClick={saveEditReg} className="text-green-600 hover:text-green-700"><Check size={12} /></button>
                <button onClick={cancelEditReg} className="text-slate-400 hover:text-slate-600"><X size={12} /></button>
              </div>
            ) : (
              <div className="flex items-center gap-1">
                <AlertTriangle size={9} className={reg ? "text-amber-400" : "text-slate-200"} />
                <span className="text-[10px] text-slate-400">
                  {reg ? `Alerta queda >${reg}%` : "Sem alerta queda"}
                </span>
                <button onClick={startEditReg} className="text-slate-300 hover:text-amber-400 transition-colors ml-0.5">
                  <Pencil size={9} />
                </button>
              </div>
            )}
          </div>
        )}
        <div className="text-[9px] text-slate-400 mt-1">{summary.count} deal{summary.count !== 1 ? "s" : ""} ativos</div>
      </div>
    </button>
  );
}

// ---------------------------------------------------------------------------
// Pagina principal
// ---------------------------------------------------------------------------
export default function MrrDashboard() {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [weeks, setWeeks] = useState(26);
  const [loading, setLoading] = useState(true);
  const [visibleIds, setVisibleIds] = useState(null); // null = todos

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/crm/mrr-dashboard?weeks=${weeks}`);
      setData(r.data);
      if (!visibleIds) {
        setVisibleIds(new Set((r.data.series || []).map((s) => s.id)));
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const saveMeta = async (seriesId, value) => {
    if (seriesId === "__total__") {
      toast.info("A meta total e calculada automaticamente como a soma das metas dos funis.");
      return;
    }
    try {
      await api.put(`/pipelines/${seriesId}/mrr-meta`, { mrr_meta: value });
      toast.success("Meta atualizada.");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const saveRegressao = async (seriesId, value) => {
    try {
      await api.put(`/pipelines/${seriesId}/mrr-meta`, { mrr_regressao_pct: value });
      toast.success(`Alerta de regressao configurado: >${value}%`);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };
  useEffect(() => { load(); }, [weeks]); // eslint-disable-line
  useAutoRefresh(load);

  const toggleSeries = (id) => {
    setVisibleIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        if (next.size === 1) return prev; // manter sempre 1 visivel
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const series = data?.series || [];
  const summary = data?.summary || {};
  const hasMultiPipeline = (data?.pipelines || []).length > 1;

  return (
    <>
      <PageHeader
        overline="CRM"
        title="Dashboard MRR"
        subtitle="Evolucao historica do MRR do pipeline por funil"
        action={
          <div className="flex items-center gap-2">
            {/* Seletor de periodo */}
            <div className="flex rounded-lg border border-slate-200 overflow-hidden bg-white">
              {PERIODS.map((p) => (
                <button key={p.weeks} onClick={() => setWeeks(p.weeks)}
                  data-testid={`period-${p.weeks}`}
                  className={`px-3 py-1.5 text-[12px] font-medium transition-colors ${weeks === p.weeks ? "bg-navy text-white" : "text-slate-500 hover:bg-slate-50"}`}>
                  {p.label}
                </button>
              ))}
            </div>
            <button onClick={load} data-testid="mrr-refresh"
              className="p-2 rounded-lg border border-slate-200 bg-white text-slate-500 hover:text-navy transition-colors">
              <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
            </button>
            <button onClick={() => nav("/app/crm/oportunidades")}
              className="flex items-center gap-1.5 px-3 py-1.5 text-[12px] rounded-lg border border-slate-200 bg-white text-slate-500 hover:text-navy">
              <ArrowLeft size={13} /> Funil
            </button>
          </div>
        }
      />

      {loading && !data && (
        <div className="flex items-center justify-center h-64 text-slate-400 text-sm">A carregar dados...</div>
      )}

      {data && (
        <>
          {/* Cards de resumo */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-2 mb-4">
            {series.map((s, si) => {
              const color = SERIES_COLORS[si % SERIES_COLORS.length];
              const sum = summary[s.id] || { mrr: 0, delta: 0, pct: 0, count: 0, mrr_meta: 0, pct_meta: null };
              return (
                <SummaryCard key={s.id} id={s.id} nome={s.nome} summary={sum} color={color}
                  active={visibleIds?.has(s.id)}
                  canEdit={s.id !== "__total__"}
                  onClick={() => toggleSeries(s.id)}
                  onSaveMeta={saveMeta}
                  onSaveRegressao={saveRegressao} />
              );
            })}
          </div>

          {/* Grafico principal */}
          <div className="bg-white rounded-xl border border-slate-200 p-4 mb-4">
            <div className="flex items-center justify-between mb-3">
              <div>
                <h3 className="text-[13px] font-semibold text-navy">Evolucao do MRR</h3>
                <p className="text-[11px] text-slate-400">Ultimas {weeks} semanas - linhas tracejadas finas = metas definidas</p>
              </div>
              <div className="flex items-center gap-3 flex-wrap justify-end">
                {series.map((s, si) => {
                  const color = SERIES_COLORS[si % SERIES_COLORS.length];
                  const active = visibleIds?.has(s.id);
                  return (
                    <button key={s.id} onClick={() => toggleSeries(s.id)}
                      className={`flex items-center gap-1 text-[10px] transition-opacity ${active ? "" : "opacity-30"}`}>
                      <span className="w-4 h-0.5 inline-block rounded-full" style={{ background: color }} />
                      <span className="text-slate-500">{s.nome}</span>
                    </button>
                  );
                })}
              </div>
            </div>
            {visibleIds && visibleIds.size > 0 && (
              <LineChart series={series} visibleIds={visibleIds} metas={data?.metas || {}} width={900} height={300} />
            )}
          </div>

          {/* Tabela de valores recentes */}
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <h3 className="text-[13px] font-semibold text-navy mb-3">Ultimas 8 semanas</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-[11px]">
                <thead>
                  <tr className="border-b border-slate-100">
                    <th className="text-left py-1.5 px-2 text-slate-400 font-medium uppercase tracking-wider text-[9px]">Semana</th>
                    {series.map((s, si) => (
                      <th key={s.id} className="text-right py-1.5 px-2 font-medium text-slate-500"
                        style={{ color: SERIES_COLORS[si % SERIES_COLORS.length] }}>
                        {s.nome}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {[...(series[0]?.points || [])].reverse().slice(0, 8).map((_, ri) => {
                    const i = (series[0]?.points?.length || 0) - 1 - ri;
                    return (
                      <tr key={i} className={`border-b border-slate-50 ${ri === 0 ? "bg-slate-50/60 font-semibold" : ""}`}>
                        <td className="py-1.5 px-2 text-slate-500 font-mono">
                          {series[0].points[i]?.week}
                          {ri === 0 && <span className="ml-1 text-[8px] bg-blue text-white rounded px-1 py-0.5">atual</span>}
                        </td>
                        {series.map((s, si) => {
                          const pt = s.points[i];
                          return (
                            <td key={s.id} className="text-right py-1.5 px-2 font-mono">
                              <span style={{ color: SERIES_COLORS[si % SERIES_COLORS.length] }}>
                                {eur(pt?.mrr ?? 0)}
                              </span>
                              {pt && !pt.real && <span className="text-amber-400 ml-0.5 text-[8px]" title="estimado">~</span>}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </>
  );
}
